import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { RemotionGraphic } from '../components/graphics/RemotionGraphics';

const { FiHeart, FiHome, FiUsers, FiSun, FiTarget, FiEye, FiSmile } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: "Genuine Care",
      description: "We believe care should come from the heart. Every interaction is filled with warmth, empathy, and authentic compassion."
    },
    {
      icon: FiHome,
      title: "Home First",
      description: "There's no place like home. We're committed to helping you stay in the place where you feel most comfortable and happy."
    },
    {
      icon: FiUsers,
      title: "Family Connection",
      description: "We become part of your extended family, building relationships that go beyond professional care to genuine friendship."
    },
    {
      icon: FiSun,
      title: "Joy in Every Day",
      description: "Life should be filled with moments of happiness. We focus on bringing smiles, laughter, and joy to your daily routine."
    }
  ];

  return (
    <>
      <Helmet>
        <title>About KCG - Your Home Care Family | Community-Centered Care</title>
        <meta name="description" content="Learn about KCG's mission to bring family-like care to your home. We're more than caregivers - we're your extended family in the community." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <div className="flex items-center space-x-3 text-orange-600 mb-4">
                  <SafeIcon icon={FiSun} className="w-6 h-6" />
                  <span className="font-medium">Our Story</span>
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                  We're Your <span className="text-orange-600 block">Care Family</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  More than just a care service, we're neighbors, friends, and extended family members who happen to be really good at helping you live your best life at home.
                </p>
              </div>

            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-3xl shadow-2xl overflow-hidden border border-orange-100 bg-orange-50">
                <RemotionGraphic
                  variant="about-nursing-home"
                  className="w-full aspect-[100/73]"
                  ariaLabel="Animated illustration of nursing home care at home"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiTarget} className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Heart</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                We believe that everyone deserves to feel loved, valued, and at home - literally. Our mission is to bring the warmth of family into professional care, creating relationships that enrich lives and bring joy to everyday moments.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiEye} className="w-6 h-6 text-amber-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Dream</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                We dream of a community where aging at home isn't just possible, but joyful. Where every person has access to care that feels like having a loving family member nearby, ready to help, listen, and share in life's precious moments.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Guides Us Every Day</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These aren't just words on a wall - they're the principles that shape every interaction, every decision, and every moment of care we provide.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 text-center border border-orange-100"
              >
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="rounded-3xl shadow-2xl overflow-hidden border border-orange-100 bg-orange-50">
                <RemotionGraphic
                  variant="about-elderly-care"
                  className="w-full aspect-[1069/848]"
                  ariaLabel="Animated illustration of a caregiver supporting an older person in a wheelchair"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold text-gray-900">How We Began</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                KCG is the trading name of Kabod Care Group. Our approach centres on respectful support and clear communication with the people we care for and their families.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We aim to understand each person’s routines, preferences and goals before agreeing a plan of support.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Community Promise</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're committed to being more than a care provider - we're active community members working to make aging at home a joyful experience for everyone.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiHeart} className="w-10 h-10 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quality First</h3>
              <p className="text-sm text-gray-600">Every interaction is guided by love and respect.</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUsers} className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Community Connected</h3>
              <p className="text-sm text-gray-600">We're active in local events and initiatives.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiHome} className="w-10 h-10 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Home-Centered</h3>
              <p className="text-sm text-gray-600">Prioritizing families staying together at home.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiSmile} className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Joy Focused</h3>
              <p className="text-sm text-gray-600">Bringing happiness to every single day.</p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
